// SPEC-PATROL-PRIZE-PREFILL-REPLACE-VISIBLE-FIX-01 (D-094):
// 巡回プレフィル用 prev を単一 booth の rows から「合成」するピュア関数。
//
// 真因: SPEC-LF1-HISTORY-FIX-05 が baseline fetch を entry_type='patrol' に限定した結果、
//   入替(replace)で変えた景品名の「最新値」まで道連れで消え、再入店で旧景品がプレフィル→
//   スタッフがメーターだけ入れて保存すると defaultsFromPrev で旧景品が新 patrol 行に焼かれ DB 汚染ループ化。
//
// 設計 (景品系=全 entry_type 最新 / メーター系=patrol 限定 の分離):
//   - 景品/set 系  = 全 entry_type の複合ソート最新行 (= 入替後の新景品)。
//   - メーター/在庫系 = patrol 限定の最新行 (= replace の in_meter=0 を混入させない)。
// prevHold(tier-0) / tier-2(IDB) / tier-3(getLastReadingForBooth) の 3 経路すべてが本関数を通すこと
// (片割れ取り残し禁止、HEATMAP-05 教訓)。

// prev から採用する景品/set 系キー (メーター系はこのリストに含めない = base=patrol 行を維持)。
// SPEC-PRIZE-ID-MULTI-ROW-V1 (D-495) ITEM_3: prize_id_2 / prize_id_3 を追加。行2/3 の紐付けも前回値として運ぶ
//   (運ばないと、保存はできるのに次に開くと未選択に戻り D-490 のバッジがまた誤発火する)。
//   D-426 のキーごと走査と D-350 の base 選択は 1 バイトも変えない(キーを足すだけ)。
// SPEC-METER-EXCHANGE-BOUNDARY-UNIFY-01 (D-984) ITEM_A: set_o(メモ)をこのリストから外す。
//   ★真因(D-978): set_o は「今回の気づき」を書く自由メモ欄なのに、他の set_a/c/l/r(設定値、
//     前回から引き継いで当然)と同列にここへ混ざっていたため、交換宣言の文面(「メーター交換」)が
//     次回巡回のメモ入力欄にそのままプリフィルされ、parseMeterNote が再び event:'reset' を
//     返して交換パネルが再発火していた(KKY01-M03-B01 で実測再現: 9/30 保存→10/2 同文面で再出現)。
//   ★ここから外すのは「古い行から繰り上げる(override)」経路を止めるため(D-426 の仕組み)。
//     ただし base 自身が持つ set_o は `{...base}` の無条件 spread でも乗るため、それは
//     buildPrevFromRows 末尾の `delete prev.set_o` で別途止めている(実測で判明、両方必要)。
//     PatrolBoothInputPage.jsx の noteMeter は現在のメモ入力欄の state(setO)を都度 parseMeterNote
//     するだけで touched.setO を見ていない(buildOptionalPatch L908)ため、メモが毎回空から始まれば
//     noteMeter.event は「今回ユーザーが実際に打った文面」からしか発火しなくなる
//     (保存ロジック buildOptionalPatch 自体は 1 バイトも変えていない = ど安定遵守)。
//   ★set_a/c/l/r(設定値の引き継ぎ)は変えない。影響は set_o(メモ)だけ。
import { dayExchangeState } from './meterBoundary'

export const PREV_PRIZE_KEYS = [
  'prize_id', 'prize_id_2', 'prize_id_3',
  'prize_name', 'prize_name_2', 'prize_name_3',
  'prize_cost', 'prize_cost_1', 'prize_cost_2', 'prize_cost_3',
  'set_a', 'set_c', 'set_l', 'set_r',
]

// SPEC-PATROL-SAME-DAY-REPLACE-NEW-STOCK-V1 (D-954):
//   carriedForwardOnly の「素通し」判定で比較する在庫系列(複数スロット機は _2/_3 も見る)。
const CARRY_STOCK_KEYS = ['prize_stock_count', 'stock_2', 'stock_3']
// 在庫値の比較。null/undefined は「未使用スロット」として同値扱い、それ以外は数値一致で比較。
const sameStockValue = (a, b) => {
  const an = a == null ? null : Number(a)
  const bn = b == null ? null : Number(b)
  return an === bn
}

// rows: 単一 booth の meter_readings 行配列 (順不同可、本関数内で複合ソート)。
// 返り値: 合成済み prev オブジェクト (rows 空 → null)。
export function buildPrevFromRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null
  const sorted = [...rows].sort((a, b) => {
    const pd = String(b?.patrol_date ?? '').localeCompare(String(a?.patrol_date ?? ''))
    if (pd !== 0) return pd
    return String(b?.created_at ?? b?.read_time ?? '').localeCompare(String(a?.created_at ?? a?.read_time ?? ''))
  })
  const latestAny = sorted[0] // 景品系の供給源 (replace 含む最新)
  // SPEC-PATROL-RESTOCK-CARRY-FIX-01 (D-350) DEFECT_A: base を「メーターが有効な最新行(entry_type 不問)」にする。
  //   従来は latestPatrol 固定で、景品差替と同時補充した replace 行の在庫/補充(PREV_PRIZE_KEYS 外)が prev に乗らなかった。
  //   有効の定義 = in_meter が null でなく Number(in_meter) !== 0(D-094 の「replace の in_meter=0 混入」を値で弾く)。
  //   out_meter は判定に使わない(patrol 行でも out_meter null が正常なため)。
  //   フォールバック: 有効メーター行なし → 従来どおり latestPatrol → それも無ければ latestAny(null 回帰を作らない)。
  const meterValid = (r) => r?.in_meter != null && Number(r.in_meter) !== 0
  // SPEC-PATROL-REPLACE-STEALS-RESTOCK-BASELINE-V1 (D-577):
  //   ★唐津 KRT01-M03-B03(2026-09-04)の実例。坂本チームが 4 人でラウンドしていて、
  //     メーター入力の人が +10 補充を入れた patrol 行(01:51)の後に、
  //     設定だけ変えた人の replace 行(02:32)が入った。
  //     ★この replace 行は in_meter を patrol 行と同じ値のまま携行しているだけで、
  //       新しい実測を 1 つも持っていない。なのに created_at が新しいだけで base に選ばれ、
  //       その prize_restock_count=0 が先発の +10 を黙って隠していた(270+10 が 270 に戻る)。
  //   ★「素通しの行」= 同じ巡回日のより早い行と in_meter が同じで、自分では補充を持たない行。
  //     こういう行は base にしない。景品/set 系は今までどおり全 entry_type の最新から採るので、
  //     設定変更そのものは反映される(この行を無視するのではなく、メーター/在庫の基準にしないだけ)。
  //   ★D-350 は壊さない。replace 行が自分で入れた補充を持っていれば「素通し」ではないので base のまま。
  //     メーターが動いている後発行も「素通し」ではないので、払い出しで在庫が減った日を巻き戻さない。
  //
  // SPEC-PATROL-SAME-DAY-REPLACE-NEW-STOCK-V1 (D-954):
  //   ★菊陽 KKY01-M03-B01 の実例(2026-09-28)。同じ巡回内で先に patrol(在庫3)、続けて
  //     入替 replace(在庫12・景品変更)を保存した。replace は in_meter を patrol 行と同じ値の
  //     まま携行しているだけなので、上の D-577 判定だと「素通しの行」に誤判定され、
  //     次回巡回(9/30)の base に patrol(在庫3)が選ばれた
  //     → theoreticalStock(3, 0, out, curOut) が -1 を出した(本来は replace の 12 から 8)。
  //   ★D-577 が「素通し」とみなしてよいのは、その行が in_meter だけでなく
  //     在庫(prize_stock_count / stock_2 / stock_3)も先行行と同じ = 本当に何も
  //     新しい申告を持っていない行だけ。在庫が先行行と違えば、それは新しい起点(入替)
  //     なので base 候補に残す。
  //   ★唐津のケース(設定だけの replace、在庫 270===270)は本追加後も一致するので
  //     従来どおり素通し=除外のまま(D-577 AC1 を壊さない)。
  //
  // SPEC-PATROL-SAME-DAY-REPLACE-NEW-STOCK-V2 (D-954b、gate_3 差し戻し是正):
  //   ★司令塔の本番実測(直近60日・同日/同 in_meter/自分の補充0 の replace 149件)で、
  //     35件が「景品だけ違い、在庫は先行行の補充前の値のまま」だった
  //     (例 TKT01-M03-B04: 先行 121+補充12 → replace 在庫121・景品名違い)。
  //     V1(景品識別も比較条件に入れる)だとこの 35 件で「景品が違う→素通しではない→
  //     replace を base 採用」になり、先行行の補充 12 が消える = D-577 の再発。
  //   ★直し方: 景品識別(旧 CARRY_PRIZE_IDENTITY_KEYS)の比較を外し、在庫系列
  //     (CARRY_STOCK_KEYS)の一致だけを追加条件にする。景品が変わっていても在庫が
  //     先行行と同じなら「新しい実測を持たない行」として素通し=先行行の補充を守る。
  //     在庫が先行行と違えば(菊陽の 3→12 のように)従来どおり base 採用。
  //   ★景品(set 系含む)は PREV_PRIZE_KEYS 側で全 entry_type の最新から別途採るので、
  //     ここで識別比較を外しても入替後の新景品自体は失われない(D-094/D-426 不変)。
  const carriedForwardOnly = (r) => (
    !Number(r?.prize_restock_count) &&
    sorted.some(o => (
      o !== r &&
      String(o?.patrol_date ?? '') === String(r?.patrol_date ?? '') &&
      String(o?.created_at ?? o?.read_time ?? '') < String(r?.created_at ?? r?.read_time ?? '') &&
      o?.in_meter != null && Number(o.in_meter) === Number(r.in_meter) &&
      CARRY_STOCK_KEYS.every(k => sameStockValue(o[k], r[k]))
    ))
  )
  const base = sorted.find(r => meterValid(r) && !carriedForwardOnly(r))
    // ★同日の有効行が全部素通しでも null 回帰を作らない。従来の base に落ちる。
    ?? sorted.find(meterValid)
    ?? sorted.find(r => r?.entry_type === 'patrol')
    ?? latestAny
  // base = メーター有効な最新行 (メーター/在庫/identity はこちら)。景品/set 系のみ上書きする。
  const prev = { ...base }
  // SPEC-PREV-PRIZE-KEYS-ABSENT-FALLBACK-V1 (D-426) ITEM_1: latestAny 無条件代入をやめ、キーごとに sorted を
  //   先頭から走査し「そのキーを present(Object.prototype.hasOwnProperty.call)に持つ最初の行」から採る。
  //   値が null の行も present=「意図的に空にした」ため、null を採用して探索を打ち切る。どの行もそのキーを
  //   持たなければ prev にそのキーを作らない(undefined を明示代入しない=「触っていない」を保つ)。
  //   base 選択・ソート順・PREV_PRIZE_KEYS は不変(メーター/在庫/補充の振る舞いは1バイトも変えない)。
  for (const k of PREV_PRIZE_KEYS) {
    const src = sorted.find(r => r != null && Object.prototype.hasOwnProperty.call(r, k))
    if (src) prev[k] = src[k]
  }
  // SPEC-METER-EXCHANGE-BOUNDARY-UNIFY-01 (D-984) ITEM_A: set_o(メモ)は base の素の spread(`{...base}`)
  //   経由でも必ず落とす。★実測で判明: set_o を PREV_PRIZE_KEYS から外すだけでは直らない
  //   (PREV_PRIZE_KEYS は「base が持たないキーを古い行から繰り上げる」ための上書き専用で、
  //   base 自身が持つ set_o は `{...base}` の無条件 spread でそのまま prev に乗る。base は通常
  //   「直近の有効行」= 前回巡回で書いたメモそのものを持つ行なので、ここで消さない限り
  //   前回の交換宣言の文面が毎回そのまま次回フォームへ出続ける=D-978 が再現する)。
  //   ★他の列(in_meter/out_meter/景品/set_a〜r等)は base からの継承を変えない。影響は set_o のみ。
  delete prev.set_o
  // SPEC-METER-EXCHANGE-REPORT-01 (D-994): メーター交換/一周の報告は「base と同じ日」の分だけを載せる。
  //   ★別の日の報告は次の訪問へ絶対に持ち越さない(D-978 の set_o と同じ事故を作らない)。巡回画面は
  //     prev.patrol_date が今日のときだけこれを「今日の報告」として読み戻す。
  //   ★同じ日の中では fn_meter_exchange_rows と同じ規則(null でない行のうち実効時刻最新)で採る。
  delete prev.meter_exchanges
  const exState = dayExchangeState(rows, base?.patrol_date)
  if (exState !== undefined) prev.meter_exchanges = exState
  return prev
}

/**
 * SPEC-PATROL-REPLACE-STEALS-RESTOCK-BASELINE-V1 (D-577) ITEM_2:
 * 同じ巡回日に複数人がこのブースへ保存したかを見る。
 * ★ひろ「黙って消えるのが最悪」。合成で拾い切れない衝突は画面に出して現場が手で直せるようにする。
 * ★見るのは一番新しい巡回日だけ。過去の日の衝突を今日の警告にしない。
 * ★1 人が複数回保存しただけ(自分の直し)は衝突にしない。
 * ★保存者が分からない行しか無ければ警告しない(嘘の衝突を作らない)。
 * @param {Array<object>} rows 単一 booth の meter_readings 行配列
 * @returns {{patrol_date: string, rows: number, savers: string[]}|null}
 */
export function detectSameDayMultiSave(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null
  const dates = rows.map(r => String(r?.patrol_date ?? '')).filter(Boolean)
  if (dates.length === 0) return null
  const latestDate = dates.sort().at(-1)
  const sameDay = rows.filter(r => String(r?.patrol_date ?? '') === latestDate)
  if (sameDay.length < 2) return null
  // ★created_by / staff_id / recorded_by のどれかを保存者として読む(列名は経路で揺れる)。
  const savers = [...new Set(
    sameDay.map(r => r?.created_by ?? r?.staff_id ?? r?.recorded_by).filter(Boolean).map(String),
  )]
  if (savers.length < 2) return null
  return { patrol_date: latestDate, rows: sameDay.length, savers }
}

/**
 * SPEC-PATROL-MULTISAVE-WARN-TIER3-V1 (D-578) ITEM_1:
 * 一番新しい巡回日の行だけを返す。★2 行以上あるときだけ返す(1 行なら衝突しようがない)。
 * ★保存者を引きに行く価値があるかの足切りに使う(毎回問い合わせない)。
 */
export function sameDayRowsOf(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const dates = rows.map(r => String(r?.patrol_date ?? '')).filter(Boolean)
  if (dates.length === 0) return []
  const latest = dates.sort().at(-1)
  const sameDay = rows.filter(r => String(r?.patrol_date ?? '') === latest)
  return sameDay.length >= 2 ? sameDay : []
}

/**
 * SPEC-PATROL-MULTISAVE-WARN-TIER3-V1 (D-578) ITEM_1:
 * reading_id で突き合わせて保存者を後から乗せる。
 * ★baseline の RPC も tier-3 の SELECT も保存者の列を運んでいないので、別建てで引いた値を合流させる。
 * ★元の行は書き換えない(合成用のコピーを返す)。
 * ★行に既に保存者が乗っていればそちらを優先する(上書きしない)。
 */
export function withSavers(rows, saverById) {
  if (!Array.isArray(rows)) return []
  const map = saverById ?? {}
  return rows.map(r => {
    const known = r?.created_by ?? r?.staff_id ?? r?.recorded_by
    if (known) return r
    const found = map[String(r?.reading_id ?? '')]
    return found ? { ...r, created_by: found } : r
  })
}
